import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuditorInvoices, type AuditorInvoicesQuery } from "../api/auditorApi";
import type { CompanyBillingInvoice } from "../api/types";
import { invoices as demoInvoices } from "../data";
import { getAuthToken } from "../auth";
import type { InvoiceReviewStatus } from "../types";

import { resolveInvoiceNatureKey } from "../components/InvoiceNatureBadge";

export type AuditorInvoiceRow = {
  insurer?: string;
  deadline?: import("../api/types").InvoiceDeadline | null;
  id: string;
  displayId: number;
  reference: string;
  hospital: string;
  company: string;
  fileName: string;
  amount: number;
  submittedDate: string;
  createdAt: string;
  status: "pending" | "approved" | "rejected" | "paid";
  reviewStatus: InvoiceReviewStatus;
  invoiceNature?: "forfait_mensuel" | "hors_forfait" | null;
  isForfaitMensuel?: boolean;
};

function mapApiRow(row: CompanyBillingInvoice): AuditorInvoiceRow {
  const created = row.created_at ? new Date(row.created_at) : null;
  const submittedDate = created ? created.toLocaleDateString("fr-FR") : "—";
  const statusLower = (row.status || "").toLowerCase();
  const nature = resolveInvoiceNatureKey({
    invoiceNature: row.invoice_nature,
    isForfaitMensuel: row.is_forfait_mensuel,
  });

  let reviewStatus: InvoiceReviewStatus = "inReview";
  if (statusLower === "submitted") reviewStatus = "submitted";
  else if (statusLower === "under review" || statusLower === "auditstarted") {
    reviewStatus = "inReview";
  }

  return {
    id: `auditor-inv-${row.id}`,
    displayId: row.id,
    reference: row.reference,
    hospital: row.hospital,
    company: row.company || row.label,
    fileName: row.label || row.reference,
    amount: row.amount_usd,
    submittedDate,
    createdAt: submittedDate,
    status: "pending",
    reviewStatus,
    invoiceNature: nature,
    isForfaitMensuel: nature === "forfait_mensuel",
    insurer: row.insurer ?? "",
    deadline: row.deadline ?? null,
  };
}

function mapDemoPending(): AuditorInvoiceRow[] {
  return demoInvoices
    .filter((invoice) => invoice.status === "pending")
    .map((invoice) => ({
      id: invoice.id,
      displayId: invoice.displayId,
      reference: invoice.reference,
      hospital: invoice.hospital,
      company: invoice.company,
      fileName: invoice.fileName,
      amount: invoice.amount,
      submittedDate: invoice.submittedDate,
      createdAt: invoice.createdAt,
      status: "pending" as const,
      reviewStatus: invoice.reviewStatus,
    }));
}

export function useAuditorInvoices(query: AuditorInvoicesQuery = { status: "pending" }) {
  const [invoices, setInvoices] = useState<AuditorInvoiceRow[]>(mapDemoPending);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setInvoices(mapDemoPending());
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const rows = await fetchAuditorInvoices(query);
      setInvoices(rows.map(mapApiRow));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les factures.");
    } finally {
      setLoading(false);
    }
  }, [query.status, query.hospital_id, query.company_id]);

  useEffect(() => {
    void load();
  }, [load]);

  const hospitalOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const row of invoices) {
      if (row.hospital) set.set(row.hospital, row.hospital);
    }
    return [{ value: "", label: "Tous" }, ...[...set.values()].map((v) => ({ value: v, label: v }))];
  }, [invoices]);

  const companyOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const row of invoices) {
      if (row.company) set.set(row.company, row.company);
    }
    return [{ value: "", label: "Toutes" }, ...[...set.values()].map((v) => ({ value: v, label: v }))];
  }, [invoices]);

  return {
    invoices,
    loading,
    error,
    usingApi,
    hospitalOptions,
    companyOptions,
    reload: load,
  };
}
